import { CompilationPlayerTrack, RadioStationTrack, StreamingPlayerError } from '@heardis/stream-contracts';
import React, { ReactNode, createContext, useContext, useRef, useState } from 'react';
import { AnalyticsEventType } from '../hooks/analytics/analytics.types';
import useAnalytics from '../hooks/analytics/useAnalytics';
import { Logger } from '../utils/logger';

const logger = new Logger('PlayerContext');

export type PlayerTrack = {
  audio?: HTMLAudioElement | undefined;
  track: RadioStationTrack | CompilationPlayerTrack;
};

export type PlayerContextProps = {
  track: PlayerTrack | undefined;
  isMuted: boolean;
  volume: number;
  currentTime: number;
  duration: number;
};

export type PlayerContextProviderProps = {
  children: React.ReactNode;
};

/** reading from the audionode */
export enum MediaStatus {
  NO_MEDIA = 'no_media',
  LOADING = 'loading',
  LOADED = 'loaded',
  BUFFERING = 'buffering',
  READY = 'ready',
  ERROR = 'error',
}

/** reading from audionode or controls */
export enum PlaybackStatus {
  EMPTY = 'empty',
  PLAYING = 'playing',
  PAUSED = 'paused',
  STOPPED = 'stopped',
}

export enum AudioNodeStatus {
  NOT_INITIALIZED = 'not_initialized',
  INITIALIZED = 'initialized',
}

export enum PlayerContextAction {
  SET_TRACK = 'SET_TRACK',
  SET_IS_MUTED = 'SET_IS_MUTED',
  SET_VOLUME = 'SET_VOLUME',
  SET_CURRENT_TIME = 'SET_CURRENT_TIME',
  SET_DURATION = 'SET_DURATION',
  SET_AUDIO_PLAYER = 'SET_AUDIO_PLAYER',
}

type Action =
  | { type: PlayerContextAction.SET_TRACK; track: PlayerTrack }
  | { type: PlayerContextAction.SET_IS_MUTED; isMuted: boolean }
  | { type: PlayerContextAction.SET_VOLUME; volume: number }
  | { type: PlayerContextAction.SET_CURRENT_TIME; currentTime: number }
  | { type: PlayerContextAction.SET_DURATION; duration: number };

export interface TrackListContext {
  tracks: CompilationPlayerTrack[];
  index: number | null;
  goTo: (index: number) => void;
}

export interface PlayerStatus {
  currentTrack?: PlayerTrack;
  currentTime: number;
  volume: number;
  duration: number | undefined;
  mediaStatus: MediaStatus;
  playbackStatus: PlaybackStatus;

  // temporary placed here as PlayerContext is already setup. Maybe move to a dedicated context
  tracklist?: TrackListContext;
}

export interface PlayerControls {
  canSeek: boolean;
  load: (track: PlayerTrack, position?: number, forcePlay?: boolean) => void;
  play: () => void;
  pause: () => void;
  seek: (progress: number) => void;
  setVolume: (volume: number) => void;
  next?: () => void;
  previous?: () => void;
}

export type PlayerContextType = PlayerStatus & PlayerControls;

export const PlayerContext = createContext<PlayerContextType>({
  volume: 1,
  currentTime: 0,
  duration: undefined,
  mediaStatus: MediaStatus.NO_MEDIA,
  playbackStatus: PlaybackStatus.EMPTY,
  canSeek: true,
  load: () => { },
  play: () => { },
  pause: () => { },
  seek: () => { },
  setVolume: () => { },
});

export const playerReducer = (state: PlayerContextProps, action: Action): PlayerContextProps => {
  switch (action.type) {
    case PlayerContextAction.SET_TRACK:
      return { ...state, track: action.track };
    case PlayerContextAction.SET_IS_MUTED:
      return { ...state, isMuted: action.isMuted };
    case PlayerContextAction.SET_VOLUME:
      return { ...state, volume: action.volume };
    case PlayerContextAction.SET_CURRENT_TIME:
      return { ...state, currentTime: action.currentTime };
    case PlayerContextAction.SET_DURATION:
      return { ...state, duration: action.duration };
    default:
      logger.warn('unhandled action', action);
      return state;
  }
};

function PlayerContextProvider({ children }: PlayerContextProviderProps): JSX.Element {
  const { trackAnalyticsEvent } = useAnalytics();
  const audioNodeRef = useRef<HTMLMediaElement>(new Audio());

  function seek(position: number) {
    if (!audioNodeRef.current) return;
    audioNodeRef.current.currentTime = position;
  }

  const [playerStatus, setPlayerStatus] = useState<PlayerContextType>({
    currentTrack: undefined,
    currentTime: 0,
    volume: 1,
    duration: 0,
    mediaStatus: MediaStatus.NO_MEDIA,
    playbackStatus: PlaybackStatus.EMPTY,
    canSeek: true,
    pause,
    play,
    seek,
    load,
    setVolume,
  });

  function play() {
    audioNodeRef.current?.play();
    /**
     * @TOASK needed to move the tracking here
     * in the onplay listener it uses an old state which was causing problems.
     * Alternative is an useEffect listening to the playback status?
     */
  }

  function pause() {
    audioNodeRef.current?.pause();
    /**
     * @TOASK needed to move the tracking here
     * in the onpause listener it uses an old state which was causing problems.
     * Alternative is an useEffect listening to the playback status?
     */
  }

  /** @TOASK needed to do it like this because the first onvolumechange isn't tracked. Otherwise I would listen to the onvolumechange */
  function setVolume(volume: number) {
    if (!audioNodeRef.current) return;
    audioNodeRef.current.volume = volume;
    setPlayerStatus((currentState) => ({ ...currentState, volume }));
  }

  const onWaiting = (event) => {
    logger.debug('onWaiting', event.currentTarget.id);
    setPlayerStatus((currentState) => ({ ...currentState, mediaStatus: MediaStatus.BUFFERING }));
  };
  const onTimeupdate = (event) => {
    const { currentTime } = event.currentTarget;
    // logger.debug('onTimeupdate', event.currentTarget.id, currentTime);
    setPlayerStatus((currentState) => ({ ...currentState, currentTime }));
  };
  const onCanPlay = (event) => {
    logger.debug('onCanPlay', event.currentTarget.id);
    setPlayerStatus((currentState) => ({ ...currentState, mediaStatus: MediaStatus.READY }));
  };
  const onPlay = (event) => {
    logger.debug('onPlay', event.currentTarget.id);
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = 'playing';
    }
    setPlayerStatus((currentState) => ({ ...currentState, playbackStatus: PlaybackStatus.PLAYING }));
    trackAnalyticsEvent({ type: AnalyticsEventType.TRACK_PLAY, track_id_current: event.currentTarget.id });
  };
  const onPause = (event) => {
    logger.debug('onPause', event.currentTarget.id);
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = 'paused';
    }
    setPlayerStatus((currentState) => ({ ...currentState, playbackStatus: PlaybackStatus.PAUSED }));
    trackAnalyticsEvent({ type: AnalyticsEventType.TRACK_PAUSE, track_id_current: event.currentTarget.id });
  };
  const onEnded = (event) => {
    logger.debug('onEnded', event.currentTarget.id);
    setPlayerStatus((currentState) => ({ ...currentState, playbackStatus: PlaybackStatus.STOPPED }));
  };
  const onError = (event) => {
    logger.warn('onError', event.currentTarget.id, event);
    /**
     * @TODO the error needs to disappear if the player started working again
     * disabled showing the error here until the other case is handled
     * */
    // setError({ code: StreamingPlayerError.FAILED_TO_LOAD_AUDIO_FILE, message: 'Failed to load audio file.' });
    // The audio element also provides a MediaError with different codes to track: https://developer.mozilla.org/en-US/docs/Web/API/MediaError
    trackAnalyticsEvent({ type: AnalyticsEventType.PLAYER_ERROR, error: StreamingPlayerError.FAILED_TO_LOAD_AUDIO_FILE, track_id: event.currentTarget.id });
    setPlayerStatus((currentState) => ({ ...currentState, mediaStatus: MediaStatus.ERROR }));
  };
  const onLoadedData = (event) => {
    const { duration, currentTime } = event.currentTarget;
    logger.debug('onLoadedData', event.currentTarget.id, event.currentTarget.duration);
    setPlayerStatus((currentState) => ({ ...currentState, currentTime, duration }));
  };
  /**
     * The AnalyticsEventType.TRACK_SEEK event could be tracked here via the onseeked event listener
     * We currently implemented it on click in the Compilation player as it will be fired to often in the radio station
     * It would be triggered on each "play" as it jumps to the current position
  */

  function load(track: PlayerTrack, position = 0, forcePlay = false) {
    logger.info('load track', track, position, forcePlay, playerStatus.playbackStatus);

    if (audioNodeRef.current) {
      logger.debug('existing audio node found. Unsubscribe:', audioNodeRef.current.id);
      audioNodeRef.current.onwaiting = null;
      audioNodeRef.current.onwaiting = null;
      audioNodeRef.current.oncanplay = null;
      audioNodeRef.current.ontimeupdate = null;
      audioNodeRef.current.onplay = null;
      audioNodeRef.current.onpause = null;
      audioNodeRef.current.onended = null;
      audioNodeRef.current.onerror = null;
      audioNodeRef.current.pause();
    }

    /**
     * @TOASK removed this logic, which was initially invented attaching a preloading audio node
     * Instead switched to changing the src of the already existing audio node
     *
     */
    // if (typeof track.audio !== 'undefined') {
    //   logger.debug('init audio node from existing:', track.track.id);
    //   audioNodeRef.current = track.audio;
    // } else {
    //   logger.debug('init audio node from url:', track.track.id);
    //   audioNodeRef.current = new Audio();
    //   audioNodeRef.current.preload = 'none';
    //   audioNodeRef.current.src = track.track.trackUri;
    // }
    logger.debug('switch to audio src from track:', track.track.id);
    audioNodeRef.current.src = track.track.trackUri;
    audioNodeRef.current.id = track.track.id;

    logger.debug('subscribed to audio events:', audioNodeRef.current.id);
    audioNodeRef.current.onloadeddata = onLoadedData;
    audioNodeRef.current.onwaiting = onWaiting;
    audioNodeRef.current.oncanplay = onCanPlay;
    audioNodeRef.current.ontimeupdate = onTimeupdate;
    audioNodeRef.current.onplay = onPlay;
    audioNodeRef.current.onpause = onPause;
    audioNodeRef.current.onended = onEnded;
    audioNodeRef.current.onerror = onError;

    logger.debug('load audio node:', audioNodeRef.current.id);
    audioNodeRef.current.load();
    audioNodeRef.current.currentTime = position;
    audioNodeRef.current.volume = playerStatus.volume;
    if (forcePlay || playerStatus.playbackStatus === PlaybackStatus.PLAYING) {
      play();
    } else {
      pause();
    }

    setPlayerStatus((currentState) => ({ ...currentState, currentTrack: track }));
  }

  return (
    <PlayerContext.Provider value={{ ...playerStatus, pause, play, load, seek, setVolume }}>
      {children}
    </PlayerContext.Provider>
  );
}

type UsePlayerContextProps = PlayerContextType;

function usePlayerContext(): UsePlayerContextProps {
  const context = useContext(PlayerContext);
  if (context === undefined) {
    throw new Error('usePlayerContext must be used within a ConfigProvider');
  }
  return context;
}

export { PlayerContextProvider, usePlayerContext };

export function MyDebugComponent({ children }: { children: ReactNode }) {
  const { currentTime, mediaStatus, playbackStatus, currentTrack } = useContext(PlayerContext);
  return (
    <ul>
      <li>{`current track: ${currentTrack?.track.artist} - ${currentTrack?.track.title}`}</li>
      <li>{`current time: ${currentTime}`}</li>
      <li>{`media state: ${mediaStatus}`}</li>
      <li>{`playback state: ${playbackStatus}`}</li>
      {children}
    </ul>
  );
}
